'use client';

import classNames from 'classnames';
import { FC, useEffect } from 'react';

import { addFavoriteApi } from '@/entities/favorites';
import { Product } from '@/entities/product';
import { useUserStore } from '@/features/user';
import { Icon } from '@/shared/ui';

import styles from './favoriteToggle.module.scss';
import { useAuthFavoriteStore } from '../../model/authFavoriteStore';
import { useFavoriteGuestStore } from '../../model/favoriteGuestStore';
import { useFavoritePendingUiStore } from '../../model/favoritePendingUiStore';
import { useFavoriteHeartState } from '../../model/useFavoriteHeartState';

interface FavoriteToggleProps {
  product: Product;
  className?: string;
  variant?: 'light' | 'dark';
  onAuthMutationDone?: () => void;
}

export const FavoriteToggle: FC<FavoriteToggleProps> = (props) => {
  const { product, className, variant = 'dark', onAuthMutationDone } = props;
  const isAuth = useUserStore((s) => s.isAuth);
  const { filled, inFavorites, pendingRemove } = useFavoriteHeartState(product);
  const guestUpsert = useFavoriteGuestStore((s) => s.upsertFromProduct);
  const authUpsert = useAuthFavoriteStore((s) => s.upsertProduct);
  const pendingAdd = useFavoritePendingUiStore((s) => s.add);
  const removePendingId = useFavoritePendingUiStore((s) => s.remove);
  const refreshPending = useFavoritePendingUiStore((s) => s.refresh);

  useEffect(() => {
    refreshPending();
  }, [refreshPending]);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (inFavorites && pendingRemove) {
      removePendingId(product.id);
      return;
    }

    if (inFavorites && !pendingRemove) {
      pendingAdd(product.id);
      return;
    }

    try {
      if (isAuth) {
        await addFavoriteApi(product.id);
        authUpsert(product);
      } else {
        guestUpsert(product);
      }
      removePendingId(product.id);
      onAuthMutationDone?.();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <button
      className={classNames(
        styles.button,
        variant === 'light' ? styles.button_light : styles.button_dark,
        filled && styles.button_filled,
        className,
      )}
      name="Избранное"
      title={filled ? 'Убрать из избранного' : 'В избранное'}
      type="button"
      onClick={handleClick}
    >
      <Icon name={filled ? 'likeFilled' : 'likeEmpty'} />
    </button>
  );
};
